import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { translateEnum } from '../../utils/i18nData';
import { DashboardPageSkeleton } from '../../components/common/Skeleton';
import { FadeInOnScroll } from '../../components/common/FadeInOnScroll';
import {
  Clock,
  Factory,
  TrendingUp,
  Calendar,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  ArrowRight,
  Activity,
  Zap,
  ListTodo,
  Network,
  Users,
  PlayCircle,
  AlertCircle,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

// Real API imports
import { getTimeEntries } from '../../api/timeEntry.api';
import { getProductionTasks } from '../../api/productionTask.api';
import { getSyncStatus, triggerSync } from '../../api/syncNew.api';
import { getMyTaskStats } from '../../api/taskAssignmentApi';
import { getOrganizationalHierarchy, getMyHierarchy } from '../../api/teamApi';

/** One page of visible entries feeds the weekly chart + recent list; keep large enough for busy weeks (backend paginates by date desc). */
const DASHBOARD_TIME_ENTRY_PAGE_SIZE = 250;

/* ═══════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════ */

/** Group time entries by day-of-week for the weekly chart */
const buildWeeklyData = (entries) => {
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const buckets = { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 };

  // Get start of current week (Monday)
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - ((dayOfWeek + 6) % 7)); // Monday
  startOfWeek.setHours(0, 0, 0, 0);

  entries.forEach((entry) => {
    const d = new Date(entry.date);
    if (d >= startOfWeek) {
      const name = dayNames[d.getDay()];
      buckets[name] += entry.hours || 0;
    }
  });

  // Return Mon-Sun order
  return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => ({
    day,
    hours: Math.round(buckets[day] * 100) / 100,
  }));
};

/** Count total members in the org hierarchy tree */
const countMembers = (nodes) => {
  if (!nodes || !Array.isArray(nodes)) return 0;
  let count = 0;
  nodes.forEach((node) => {
    if (node.type === 'member') count += 1;
    if (node.children) count += countMembers(node.children);
  });
  return count;
};

/** Count teams in the org hierarchy tree */
const countTeams = (nodes) => {
  if (!nodes || !Array.isArray(nodes)) return 0;
  let count = 0;
  nodes.forEach((node) => {
    if (node.type === 'team') count += 1;
    if (node.children) count += countTeams(node.children);
  });
  return count;
};

/* ═══════════════════════════════════════════════
   DASHBOARD
   ═══════════════════════════════════════════════ */
const Dashboard = () => {
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const { t } = useTranslation();

  const chartTooltipStyle = useMemo(
    () =>
      darkMode
        ? {
            backgroundColor: '#1f2937',
            border: '1px solid #374151',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            color: '#f3f4f6',
          }
        : {
            backgroundColor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            color: '#111827',
          },
    [darkMode],
  );

  /* ── State ── */
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [timeEntryData, setTimeEntryData] = useState({ items: [], totalCount: 0 });
  const [productionCount, setProductionCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState(null);
  const [taskStats, setTaskStats] = useState(null);
  const [orgData, setOrgData] = useState({ teams: 0, members: 0 });

  /* ── Fetch all data ── */
  const fetchDashboardData = useCallback(async () => {
    try {
      // Role-based org data: Admin sees all, others see only their teams
      const isAdmin = user?.role === 4 || user?.role === 'Admin';
      const orgFetcher = isAdmin ? getOrganizationalHierarchy : getMyHierarchy;

      const [entriesRes, prodRes, syncRes, taskRes, orgRes] = await Promise.all([
        getTimeEntries(1, DASHBOARD_TIME_ENTRY_PAGE_SIZE).catch(() => ({ isSuccess: false })),
        getProductionTasks({ pageNumber: 1, pageSize: 1 }).catch(() => ({ isSuccess: false })),
        getSyncStatus().catch(() => ({ isSuccess: false })),
        getMyTaskStats().catch(() => ({ isSuccess: false })),
        orgFetcher().catch(() => ({ isSuccess: false })),
      ]);

      // Time entries
      if (entriesRes.isSuccess && entriesRes.data) {
        setTimeEntryData({
          items: entriesRes.data.items || [],
          totalCount: entriesRes.data.totalCount || 0,
        });
      }

      // Production tasks — we only need the total count
      if (prodRes.isSuccess && prodRes.data) {
        setProductionCount(prodRes.data.totalCount || 0);
      }

      // Sync status
      if (syncRes.isSuccess && syncRes.data) {
        setSyncStatus(syncRes.data);
      }

      // Task stats
      if (taskRes.isSuccess && taskRes.data) {
        setTaskStats(taskRes.data);
      } else if (taskRes.data) {
        setTaskStats(taskRes.data);
      }

      // Org hierarchy
      if (orgRes.isSuccess && orgRes.data) {
        setOrgData({
          teams: countTeams(orgRes.data),
          members: countMembers(orgRes.data),
        });
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  /* ── Derived data ── */
  const weeklyData = useMemo(
    () => buildWeeklyData(timeEntryData.items),
    [timeEntryData.items],
  );

  const weeklyTotal = useMemo(
    () => weeklyData.reduce((sum, d) => sum + d.hours, 0).toFixed(1),
    [weeklyData],
  );

  const syncPieData = useMemo(() => {
    if (!syncStatus) return [];
    return [
      { name: t('enums.synced'), value: syncStatus.syncedEntries || 0, color: '#10B981' },
      { name: t('enums.pending'), value: syncStatus.pendingEntries || 0, color: '#F59E0B' },
      { name: t('enums.failed'), value: syncStatus.failedEntries || 0, color: '#EF4444' },
    ].filter((d) => d.value > 0);
  }, [syncStatus, t]);

  const recentEntries = useMemo(
    () => (timeEntryData.items || []).slice(0, 5),
    [timeEntryData.items],
  );

  /* ── Sync action ── */
  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await triggerSync();
      await fetchDashboardData();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  /* ── Sub-components ── */
  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-all duration-300 group">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          {subtitle && (
            <p className="text-sm mt-2 text-gray-400 dark:text-gray-500">
              {subtitle}
            </p>
          )}
        </div>
        <div className={`p-4 rounded-2xl ${color} group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const normalized = (status || 'pending').toLowerCase();
    const styles = {
      synced: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
      failed: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
    };
    const icons = {
      synced: CheckCircle,
      pending: RefreshCw,
      failed: AlertTriangle,
    };
    const StatusIcon = icons[normalized] || RefreshCw;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${styles[normalized] || styles.pending}`}>
        <StatusIcon className="w-3 h-3" />
        {translateEnum(t, normalized)}
      </span>
    );
  };

  /* ── Loading ── */
  if (loading) {
    return <DashboardPageSkeleton />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
            {t('pages.dashboard.welcomeUser', {
              name: user?.name?.split(' ')[0] || user?.firstName || t('commonUi.user'),
            })}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1.5 text-sm sm:text-base max-w-xl">
            {t('pages.dashboard.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* <button
            onClick={handleSyncNow}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2.5 bg-operis-blue hover:bg-primary-700 text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync with SAP'}
          </button> */}
          <Link
            to="/time-entries/new"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white shadow-md shadow-operis-blue/20 bg-gradient-to-r from-operis-blue to-indigo-600 hover:shadow-lg hover:shadow-operis-blue/25 hover:brightness-[1.03] transition-all duration-200"
          >
            <Clock className="w-5 h-5" aria-hidden />
            {t('pages.dashboard.logTime')}
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('pages.dashboard.statTimeEntries')}
          value={timeEntryData.totalCount}
          icon={Clock}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          subtitle={t('pages.dashboard.weeklyHoursThisWeek', { hours: weeklyTotal })}
        />
        <StatCard
          title={t('pages.dashboard.statProductionTasks')}
          value={productionCount}
          icon={Factory}
          color="bg-gradient-to-br from-purple-500 to-purple-600"
          subtitle={taskStats ? t('pages.dashboard.inProgressCount', { count: taskStats.inProgress ?? 0 }) : null}
        />
        <StatCard
          title={t('pages.dashboard.statOrgTeams')}
          value={orgData.teams}
          icon={Network}
          color="bg-gradient-to-br from-emerald-500 to-emerald-600"
          subtitle={`${orgData.members} ${t('commonUi.members')}`}
        />
        <StatCard
          title={t('pages.dashboard.statPendingSync')}
          value={syncStatus?.pendingEntries ?? 0}
          icon={RefreshCw}
          color="bg-gradient-to-br from-amber-500 to-amber-600"
          subtitle={syncStatus?.lastSyncTime
            ? `${t('commonUi.last')}: ${new Date(syncStatus.lastSyncTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : t('commonUi.neverSynced')}
        />
      </div>

      <FadeInOnScroll className="block">
        {/* Charts + Task Overview Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Weekly Hours Chart */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.dashboard.weeklyHours')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.dashboard.weeklyHoursSub')}</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Activity className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">{t('pages.dashboard.hrsTotal', { hours: weeklyTotal })}</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={weeklyData}>
              <defs>
                <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#E5E7EB'} />
              <XAxis dataKey="day" stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => t(`weekday.${v}`)} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip
                contentStyle={chartTooltipStyle}
                formatter={(value) => [`${value} ${t('commonUi.hrs')}`, t('pages.dashboard.chartHours')]}
              />
              <Area
                type="monotone"
                dataKey="hours"
                stroke="#3B82F6"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorHours)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Sync Status + Org Overview */}
        <div className="space-y-6">
          {/* Sync Status Donut */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.dashboard.sapSync')}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('pages.dashboard.totalEntriesCount', { count: syncStatus?.totalEntries ?? 0 })}
                </p>
              </div>
              <Zap className={`w-5 h-5 ${syncStatus?.isConnected ? 'text-green-500' : 'text-red-500'}`} />
            </div>
            {syncPieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie
                      data={syncPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={42}
                      outerRadius={60}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {syncPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={chartTooltipStyle} formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {syncPieData.map((item, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {item.name} ({item.value})
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400 dark:text-gray-500">
                <RefreshCw className="w-8 h-8 mb-2" />
                <p className="text-sm">{t('pages.dashboard.noSyncData')}</p>
              </div>
            )}
          </div>

          {/* Org Overview */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.dashboard.organization')}</h2>
              <Link to="/org-hierarchy" className="text-operis-blue dark:text-blue-400 hover:text-primary-700 dark:hover:text-blue-300">
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/30">
                <Network className="w-5 h-5 text-indigo-500" />
                <div>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{orgData.teams}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('commonUi.teams')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30">
                <Users className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{orgData.members}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('commonUi.members')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </FadeInOnScroll>

      <FadeInOnScroll className="block">
        {/* Time Entry Overview Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.dashboard.timeEntryOverview')}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.dashboard.timeEntryOverviewSub')}</p>
          </div>
          <Link
            to="/time-entries"
            className="flex items-center gap-1 text-operis-blue dark:text-blue-400 hover:text-primary-700 dark:hover:text-blue-300 font-medium text-sm transition-colors"
          >
            {t('commonUi.viewAll')}
            <ArrowRight className="w-4 h-4" />
          </Link>
          </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: t('pages.dashboard.statTimeEntries'), value: timeEntryData.totalCount, icon: Clock, bg: 'bg-blue-50 dark:bg-blue-900/20', iconColor: 'text-blue-500' },
            { label: t('pages.dashboard.labelSynced'), value: syncStatus?.syncedEntries ?? 0, icon: CheckCircle, bg: 'bg-emerald-50 dark:bg-emerald-900/20', iconColor: 'text-emerald-500' },
            { label: t('pages.dashboard.labelOngoing'), value: timeEntryData.items.filter(e => e.isRunning).length, icon: PlayCircle, bg: 'bg-amber-50 dark:bg-amber-900/20', iconColor: 'text-amber-500' },
            { label: t('pages.dashboard.labelCompleted'), value: timeEntryData.items.filter(e => !e.isRunning && (e.hours || 0) > 0).length, icon: CheckCircle, bg: 'bg-teal-50 dark:bg-teal-900/20', iconColor: 'text-teal-500' },
            { label: t('pages.dashboard.labelTimeLogged'), value: `${weeklyTotal}h`, icon: TrendingUp, bg: 'bg-purple-50 dark:bg-purple-900/20', iconColor: 'text-purple-500' },
            { label: t('pages.dashboard.labelPendingSync'), value: syncStatus?.pendingEntries ?? 0, icon: RefreshCw, bg: 'bg-orange-50 dark:bg-orange-900/20', iconColor: 'text-orange-500' },
          ].map(({ label, value, icon: TIcon, bg, iconColor }) => (
            <div key={label} className={`flex items-center gap-3 p-4 rounded-xl ${bg}`}>
              <TIcon className={`w-6 h-6 ${iconColor}`} />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              </div>
            </div>
          ))}
        </div>
        </div>
      </FadeInOnScroll>

      <FadeInOnScroll className="block">
        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('pages.dashboard.recentTimeEntries')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.dashboard.recentTimeEntriesSub')}</p>
            </div>
            <Link
              to="/time-entries"
              className="flex items-center gap-1 text-operis-blue dark:text-blue-400 hover:text-primary-700 dark:hover:text-blue-300 font-medium text-sm transition-colors"
            >
              {t('commonUi.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          </div>
        {recentEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 px-6 text-center text-gray-500 dark:text-gray-400">
            <div className="rounded-2xl bg-gray-100 dark:bg-gray-800/80 p-4 mb-4 ring-1 ring-gray-200/80 dark:ring-gray-700">
              <Clock className="w-10 h-10 text-gray-400 dark:text-gray-500" aria-hidden />
            </div>
            <p className="font-semibold text-gray-800 dark:text-gray-200">{t('pages.dashboard.noEntriesYet')}</p>
            <p className="text-sm mt-1 max-w-sm leading-relaxed">
              {t('pages.dashboard.noEntriesHint')}
            </p>
            <Link
              to="/time-entries/new"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-operis-blue dark:text-indigo-400 hover:underline"
            >
              {t('pages.dashboard.logFirstTime')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {recentEntries.map((entry) => (
              <div key={entry.id} className="p-6 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {entry.description || entry.timeTypeCode || t('enums.time_entry')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{t('pages.dashboard.hoursUnit', { hours: entry.hours })}</p>
                  </div>
                  {getStatusBadge(entry.syncStatus)}
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </FadeInOnScroll>

      <FadeInOnScroll className="block">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/time-entries"
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white hover:shadow-lg transition-all duration-300 group"
        >
          <Clock className="w-10 h-10 mb-4" />
          <h3 className="text-xl font-semibold mb-2">{t('pages.dashboard.quickTimeTitle')}</h3>
          <p className="text-blue-100 text-sm mb-4">{t('pages.dashboard.quickTimeDesc')}</p>
          <div className="flex items-center gap-2 text-sm font-medium">
            {t('commonUi.open')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link
          to="/production-tasks"
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white hover:shadow-lg transition-all duration-300 group"
        >
          <Factory className="w-10 h-10 mb-4" />
          <h3 className="text-xl font-semibold mb-2">{t('pages.dashboard.quickProdTitle')}</h3>
          <p className="text-purple-100 text-sm mb-4">{t('pages.dashboard.quickProdDesc')}</p>
          <div className="flex items-center gap-2 text-sm font-medium">
            {t('commonUi.open')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link
          to="/org-hierarchy"
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white hover:shadow-lg transition-all duration-300 group"
        >
          <Network className="w-10 h-10 mb-4" />
          <h3 className="text-xl font-semibold mb-2">{t('pages.dashboard.quickOrgTitle')}</h3>
          <p className="text-emerald-100 text-sm mb-4">{t('pages.dashboard.quickOrgDesc')}</p>
          <div className="flex items-center gap-2 text-sm font-medium">
            {t('commonUi.open')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
        </div>
      </FadeInOnScroll>
    </div>
  );
};

export default Dashboard;
